// run.ts — a tiny session "run" ledger for the board-by-board match summary (M3 results screen).
//
// The renderer plays one board at a time (GameScene -> ResultScene -> NEXT -> GameScene ...). To
// show a "you vs AI, board-by-board" summary without changing that flow or Core, we accumulate each
// finished board's outcome on the Phaser game registry for the current session. NEXT appends; PLAY
// AGAIN / quitting to the Menu resets it. This is pure presentation state — never Core/determinism.
import type { Scene } from 'phaser';

export interface RunBoard {
  boardIndex: number;
  topicLabel: string;
  humanScore: number;
  aiScore: number | null;
  /** board winner: 0 human, 1 AI, null draw/solo */
  winner: number | null;
  won: boolean; // human cleared the board's quota
  bestCombo: number;
  eatenCorrect: number;
  quota: number;
  /** Gauntlet threads (×1.5 "THREADED IT") the human landed on this board. Optional (older run entries
   *  and arg-less paths default to 0); summed across the match to feed the BYT3 grudge thread-streak. */
  threads?: number;
}

const RUN_KEY = 'run.boards';

export function getRun(scene: Scene): RunBoard[] {
  const arr = scene.registry.get(RUN_KEY) as RunBoard[] | undefined;
  return Array.isArray(arr) ? arr : [];
}

export function appendRun(scene: Scene, board: RunBoard): RunBoard[] {
  const next = [...getRun(scene), board];
  scene.registry.set(RUN_KEY, next);
  return next;
}

export function replaceRun(scene: Scene, boards: RunBoard[]): RunBoard[] {
  const next = boards.slice();
  scene.registry.set(RUN_KEY, next);
  return next;
}

export function resetRun(scene: Scene): void {
  scene.registry.set(RUN_KEY, []);
}

/** Roll-up totals for the run (used by the summary header + the leaderboard submit). */
export interface RunTotals {
  totalHumanScore: number;
  totalAiScore: number;
  humanBoardWins: number;
  aiBoardWins: number;
  bestCombo: number;
  boards: number;
}

export function runTotals(boards: RunBoard[]): RunTotals {
  let totalHumanScore = 0;
  let totalAiScore = 0;
  let humanBoardWins = 0;
  let aiBoardWins = 0;
  let bestCombo = 0;
  for (const b of boards) {
    totalHumanScore += b.humanScore;
    totalAiScore += b.aiScore ?? 0;
    if (b.winner === 0) humanBoardWins += 1;
    else if (b.winner === 1) aiBoardWins += 1;
    bestCombo = Math.max(bestCombo, b.bestCombo);
  }
  return {
    totalHumanScore,
    totalAiScore,
    humanBoardWins,
    aiBoardWins,
    bestCombo,
    boards: boards.length,
  };
}
